import type { ComponentType } from "react";
import SectionHead from "@/components/common/SectionHead";
import RevealStagger from "@/components/common/RevealStagger";
import { BankIcon, ChartIcon, RouteIcon } from "@/components/common/icons";
import { useLocale } from "@/i18n/useLocale";
import { landingInfraFor } from "@/content/landing";
import type { InfraCardKey } from "@/content/types";

interface InfraCard {
  key: InfraCardKey;
  num: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
}

// Every claim on this section is verifiable from the platform FAQ
// ("Who provides liquidity and execution?", "Which trading platforms
// does Flex Fund offer?"). No fabricated numbers, no fabricated
// reviews — the section's job is to surface the operational
// substance underneath the platform until real social proof
// (reviews, payout receipts, trader narratives) is available.
const INFRA_CARDS: readonly InfraCard[] = [
  { key: "stpRouting", num: "01", Icon: RouteIcon },
  { key: "tier1Banks", num: "02", Icon: BankIcon },
  { key: "metaTrader5", num: "03", Icon: ChartIcon },
];

// Phase RW.14 (2026-05-08): replaces the previous `Testimonials`
// section. Real reviews, real payout numbers, and real trader
// quotes don't exist yet (the platform is pre-launch / first
// cohort). Rather than ship fabricated social proof, the slot
// pivots to operational substance — the same trust signal
// professional traders evaluate first ("who's the broker?
// where's the liquidity coming from?"). When real testimonials
// land, this component can be replaced or composed alongside.
export default function Infrastructure(): JSX.Element {
  const t = landingInfraFor(useLocale());

  return (
    <section className="section-y-lg">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.title.line1}
              <br />
              <span className="text-brand-gradient">{t.title.line2Highlight}</span>
            </>
          }
          sub={t.sub}
        />

        <RevealStagger className="mt-10 md:mt-12 grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-3" step={80}>
          {INFRA_CARDS.map((c) => (
            // An odd card count leaves the last one orphaned in the 2-up
            // mobile grid; spanning both columns fills the row instead.
            <div
              key={c.key}
              className="glass-card glass-card-hover h-full p-4 md:p-6 last:col-span-2 lg:last:col-span-1"
            >
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10.5px] text-brand-emphasis tracking-[0.18em]">
                  {c.num}
                </div>
                <div className="font-mono text-[8.5px] md:text-[9px] tracking-[0.14em] md:tracking-[0.16em] text-fg-dim uppercase">
                  {t.cards[c.key].eyebrow}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 md:gap-3 mt-3 md:mt-3.5">
                <h3
                  className="font-display font-semibold text-[17px] md:text-[22px]"
                  style={{ letterSpacing: "-0.015em" }}
                >
                  {t.cards[c.key].title}
                </h3>
                <div
                  className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-[10px] border text-brand-emphasis shrink-0"
                  style={{
                    background: "rgb(var(--brand) / 0.10)",
                    borderColor: "rgb(var(--brand) / 0.28)",
                  }}
                  aria-hidden="true"
                >
                  <c.Icon size={16} className="md:hidden" />
                  <c.Icon size={18} className="hidden md:block" />
                </div>
              </div>
              <p className="text-fg-muted mt-2 md:mt-2.5 text-[12.5px] md:text-sm leading-[1.5] md:leading-[1.55]">
                {t.cards[c.key].body}
              </p>
            </div>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}
